import React from 'react';
import type { PiV2Plan } from '../../../patient-insurance/typesV2';
import { PI_V2_ROLE_LABEL, piV2Money, piV2Pct } from '../../../patient-insurance/utilsV2';
import { Pill } from '../../Pill/Pill';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientInsuranceBenefitsV2Props {
  plans: PiV2Plan[];
}

/**
 * One compact row per plan, all plans always visible.
 *
 * This is the deliberate exception to the per-plan switcher: "how much is
 * left" is the question asked most, and it is asked about every plan at once.
 * Hiding the secondary's remaining benefit behind a tab would mean clicking to
 * answer the most common question on the page.
 *
 * A discount plan (annual max 0) is shown as having no benefit to report —
 * not as "$0 remaining", which reads like an exhausted plan.
 */
export function PatientInsuranceBenefitsV2({ plans }: PatientInsuranceBenefitsV2Props) {
  return (
    <PatientProfileSectionCard
      icon="dollar"
      title="Benefits remaining"
      accent="var(--success)"
      subtitle={plans.length === 1 ? 'One plan' : `${String(plans.length)} plans`}
    >
      <div className="flex flex-col">
        {plans.map((plan, i) => {
          const b = plan.benefits;
          const noBenefit = b.annualMax <= 0;
          const usedPct = piV2Pct(b.used, b.annualMax);
          const dedPct = piV2Pct(b.deductibleMet, b.deductible);
          const dedMet = b.deductible > 0 && b.deductibleMet >= b.deductible;

          return (
            <div
              key={plan.id}
              className="py-3.5"
              style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
            >
              <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]">
                  {PI_V2_ROLE_LABEL[plan.role]}
                </span>
                <span className="text-[12.5px] font-semibold text-[var(--ink-1)]">{plan.carrier}</span>
                <span className="ml-auto text-[11px] text-[var(--ink-4)]">
                  Resets {b.planYearEnd} · {b.daysLeftInPlanYear} days
                </span>
              </div>

              {noBenefit ? (
                <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                  No annual maximum — this plan pays nothing on the patient&apos;s behalf.
                </p>
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="font-[var(--font-display)] text-[26px] font-bold leading-none tracking-[-0.03em] text-[var(--success)]">
                      {piV2Money(b.remaining)}
                    </span>
                    <span className="text-[12px] text-[var(--ink-3)]">remaining</span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">
                    {piV2Money(b.used)} used of {piV2Money(b.annualMax)} annual maximum
                  </div>
                  <div className="mt-2 h-[7px] overflow-hidden rounded-full bg-[var(--cream-2)]">
                    <div
                      className="h-full rounded-full bg-[var(--success)]"
                      style={{ width: `${String(usedPct)}%` }}
                    />
                  </div>
                  {b.deductible > 0 ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[var(--ink-4)]">
                      <span>
                        Deductible{' '}
                        <span className="font-[var(--font-mono)] text-[var(--ink-2)]">
                          {piV2Money(b.deductibleMet)} of {piV2Money(b.deductible)} met
                        </span>
                      </span>
                      {dedMet ? (
                        <Pill tone="success" dot>
                          Met
                        </Pill>
                      ) : (
                        <span>{b.deductibleMet > 0 ? 'Partially met' : 'Not met'}</span>
                      )}
                    </div>
                  ) : null}
                  {b.deductible > 0 ? (
                    <div className="mt-1.5 h-[4px] overflow-hidden rounded-full bg-[var(--cream-2)]">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${String(dedPct)}%`,
                          background: dedMet ? 'var(--success)' : 'var(--gold)',
                        }}
                      />
                    </div>
                  ) : null}
                  {b.familyDeductible > 0 ? (
                    <div className="mt-1.5 text-[11px] text-[var(--ink-4)]">
                      Family deductible{' '}
                      <span className="font-[var(--font-mono)] text-[var(--ink-2)]">
                        {piV2Money(b.familyMet)} of {piV2Money(b.familyDeductible)} met
                      </span>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
        })}
      </div>
    </PatientProfileSectionCard>
  );
}
